import type { CategoryDefaults, Suggestion, WeatherSnapshot } from '@wardrobe/shared';

/**
 * Läuft die Foto-Analyse — und wenn nein, woran liegt es? „Aus" behebt ein Schalter in
 * „Mehr", „kein Schlüssel" nur ein Eintrag im Stack. Ohne diese Unterscheidung riet die
 * Meldung im Erfassungsbildschirm.
 */
export interface Analysestatus {
  enabled: boolean;
  reason: 'ausgeschaltet' | 'kein_schluessel' | null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * §11: Offline-Schreiben ist ein ausdrückliches Nicht-Ziel. Es gibt keine lokale
 * Warteschlange und keine optimistischen Erfolgsmeldungen — schlägt ein Request fehl,
 * sagt die App das, statt so zu tun, als sei es gespeichert.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // JSON-Body bekommt den content-type gesetzt, FormData nicht: dort muss der Browser
  // die multipart-Boundary selbst bestimmen.
  const jsonBody = init?.body !== undefined && !(init.body instanceof FormData);
  const headers: HeadersInit = jsonBody
    ? { 'content-type': 'application/json', ...init?.headers }
    : (init?.headers ?? {});

  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, headers, credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'OFFLINE', 'Keine Verbindung zum Server.');
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body: unknown = text === '' ? null : safeJson(text);

  if (!res.ok) {
    const err = body as { error?: string; message?: string } | null;
    throw new ApiError(
      res.status,
      err?.error ?? 'UNKNOWN',
      err?.message ?? `Fehler ${res.status}`,
    );
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export interface ItemAvailability {
  available: boolean;
  reasons: string[];
}

export interface Item {
  id: string;
  name: string;
  category: string;
  subcategory: string | null;
  layer: string;
  brand: string | null;
  size: string | null;
  colorPrimaryName: string | null;
  colorPrimaryHex: string | null;
  pattern: string | null;
  notes: string | null;
  photoId: string | null;
  warmthPoints: number;
  formality: number;
  rainResistance: number;
  windResistance: number;
  carePolicy: string;
  laundryGroup: string;
  washTempC: number | null;
  dryingHours: number;
  restHours: number;
  purchaseDate: string | null;
  lifecycleState: string;
  location: string;
  wearsSinceWash: number;
  wearsPerWash: number;
  wearsSinceClean: number;
  wearsPerClean: number | null;
  totalWears: number;
  lastWornAt: string | null;
  availableFrom: string | null;
  purchasePriceCents: number | null;
  condition: number | null;
  needsIroning: boolean;
  suggestable: boolean;
  availability: ItemAvailability;
  needsCleaning: boolean;
}

export interface ItemDetail extends Item {
  history: Record<string, unknown>[];
  learning: Record<string, unknown>[];
}

export interface RecommendResponse {
  date: string;
  weather: WeatherSnapshot;
  /** D-27: true, wenn der Snapshot aus dem Cache stammt und älter ist als die TTL. */
  stale: boolean;
  suggestions: Suggestion[];
  attribution: string;
}

export interface HamperEntry {
  id: string;
  name: string;
  laundry_group: string;
  care_policy: string;
  /** D-36: entscheidet über die Vorauswahl — Getragenes ja, Sauberes nur auf Wunsch. */
  lifecycle_state: string;
  /** D-37: vom Pflegeetikett abgelesen; der niedrigste Wert der Auswahl gilt. */
  wash_temp_c: number | null;
  wears_since_wash: number;
  wears_per_wash: number;
}

/** Ein gespeichertes Outfit samt seiner Slots, wie `GET /outfits` es liefert. */
export interface OutfitRow {
  id: string;
  name: string;
  notes: string | null;
  formality_target: number | null;
  slots: { id: string; slot: string; mode: string; item_id: string | null; optional: number }[];
}

/** D-34: Anlässe sind bearbeitbare Daten, keine Aufzählung im Quelltext. */
export interface OccasionRow {
  id: string;
  label: string;
  formality: number;
  position: number;
  archived: boolean;
}

export interface TripRow {
  id: string;
  name: string;
  start_date: string;
  end_date: string;
  dest_lat: number | null;
  dest_lon: number | null;
  laundry_available: number;
  status: string;
}

export interface PackingList {
  tripId: string;
  days: number;
  cycleDays: number;
  apparentC: number;
  weatherSource: 'forecast' | 'angenommen';
  rainRisk: number;
  entries: { itemId: string; name: string; slot: string; category: string; reason: string }[];
  missing: string[];
  notes: string[];
}

/** D-31: eine gepflegte Kombinierbarkeitsregel, aus Sicht des betrachteten Teils. */
export interface CompatRule {
  itemId: string;
  mode: 'only' | 'never';
  note: string | null;
  name: string;
  category: string;
  layer: string;
}

export interface CareEvent {
  id: string;
  item_id: string;
  item_name: string;
  type: string;
  vendor: string | null;
  sent_at: string;
  expected_back_at: string | null;
  cost_cents: number | null;
}

export interface PushStatus {
  /** Server hat VAPID-Schlüssel; ohne die kann gar nichts gesendet werden. */
  configured: boolean;
  enabled: boolean;
  subscriptions: number;
  publicKey: string | null;
  lastError: string | null;
}

/** Zeile aus `GET /wash-runs` — Spalten von `wash_runs` plus die Anzahl der Teile. */
export interface WashRunSummary {
  id: string;
  laundry_group: string;
  temp_c: number | null;
  program: string | null;
  started_at: string;
  finished_at: string | null;
  note: string | null;
  item_count: number;
}

export interface WearLogSummary {
  id: string;
  local_date: string;
  occasion: string;
  weather_source: string;
  thermal_vote: number | null;
}

export const api = {
  /** Ohne Anmeldung erreichbar — zeigt, welcher Stand auf dem Server laeuft. */
  health: () =>
    fetch('/healthz', { credentials: 'same-origin' }).then(
      (r) =>
        r.json() as Promise<{
          version: string;
          commit: string;
          time: string;
          builtAt: string | null;
        }>,
    ),

  authStatus: () => request<{ configured: boolean }>('/auth/status'),
  login: (pin: string) =>
    request<{ ok: true }>('/auth/login', { method: 'POST', body: JSON.stringify({ pin }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  /**
   * D-38/D-41: Kategorie-Startwerte in drei Schichten — Katalog, eigener Bestand, von
   * Hand gesetzt. `quellen` sagt je Feld, welche gewonnen hat.
   */
  defaults: () =>
    request<Record<string, CategoryDefaults & { quellen: Record<string, string> }>>('/defaults'),
  setCategoryDefaults: (category: string, patch: Record<string, unknown>) =>
    request<{ category: string; overrides: Record<string, unknown> }>(`/defaults/${category}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),
  clearCategoryDefaults: (category: string) =>
    request<{ category: string }>(`/defaults/${category}`, { method: 'DELETE' }),

  items: (params: Record<string, string> = {}) =>
    request<Item[]>(`/items?${new URLSearchParams(params).toString()}`),
  item: (id: string) => request<ItemDetail>(`/items/${id}`),
  createItem: (payload: Record<string, unknown>) =>
    request<Item & { duplicateHint: string | null; copies: number; ids: string[] }>('/items', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  updateItem: (id: string, payload: Record<string, unknown>) =>
    request<Item>(`/items/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  action: (id: string, action: string) =>
    request<{ eventId: string | null; alreadyLoggedToday?: boolean }>(
      `/items/${id}/actions/${action}`,
      { method: 'POST' },
    ),
  /** Dieselbe Aktion für viele Teile, in einer Transaktion (D-36). */
  bulkAction: (action: string, itemIds: string[]) =>
    request<{ eventIds: string[]; count: number }>(`/items/actions/${action}`, {
      method: 'POST',
      body: JSON.stringify({ itemIds }),
    }),

  recommend: (occasion: string, limit = 3) =>
    request<RecommendResponse>(`/recommend?occasion=${occasion}&limit=${String(limit)}`),

  hamper: () => request<HamperEntry[]>('/hamper'),
  startWashRun: (payload: Record<string, unknown>) =>
    request<{ id: string }>('/wash-runs', { method: 'POST', body: JSON.stringify(payload) }),
  finishWashRun: (id: string) => request<{ ok: true }>(`/wash-runs/${id}/finish`, { method: 'POST' }),
  washRuns: () => request<WashRunSummary[]>('/wash-runs'),

  createWearLog: (payload: Record<string, unknown>) =>
    request<{ id: string; skipped: string[] }>('/wear-logs', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  wearLogs: () => request<WearLogSummary[]>('/wear-logs'),
  pendingFeedback: () => request<{ date: string; logs: string[] }>('/wear-logs/pending-feedback'),
  setFeedback: (id: string, payload: Record<string, unknown>) =>
    request<{ ok: true; applied: number }>(`/wear-logs/${id}/feedback`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  outfits: {
    list: () => request<OutfitRow[]>('/outfits'),
    create: (payload: Record<string, unknown>) =>
      request<{ id: string }>('/outfits', { method: 'POST', body: JSON.stringify(payload) }),
    remove: (id: string) => request<{ ok: true }>(`/outfits/${id}`, { method: 'DELETE' }),
  },

  occasions: {
    list: (mitArchivierten = false) =>
      request<{ occasions: OccasionRow[] }>(
        `/occasions${mitArchivierten ? '?includeArchived=1' : ''}`,
      ),
    create: (label: string, formality: number) =>
      request<OccasionRow>('/occasions', {
        method: 'POST',
        body: JSON.stringify({ label, formality }),
      }),
    update: (id: string, patch: Record<string, unknown>) =>
      request<OccasionRow>(`/occasions/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    remove: (id: string) =>
      request<{ entfernt: boolean; archiviert: boolean; verwendungen: number }>(
        `/occasions/${id}`,
        { method: 'DELETE' },
      ),
  },

  trips: {
    list: () => request<TripRow[]>('/trips'),
    get: (id: string) =>
      request<{ trip: TripRow; items: { itemId: string; name: string }[] }>(`/trips/${id}`),
    create: (payload: Record<string, unknown>) =>
      request<{ id: string }>('/trips', { method: 'POST', body: JSON.stringify(payload) }),
    setStatus: (id: string, status: string) =>
      request<{ ok: true; status: string }>(`/trips/${id}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      }),
    packingList: (id: string, apparentC?: number) =>
      request<PackingList>(
        `/trips/${id}/packing-list${apparentC === undefined ? '' : `?apparentC=${String(apparentC)}`}`,
      ),
    reserve: (id: string, itemIds: string[]) =>
      request<{ ok: true; reserved: number }>(`/trips/${id}/items`, {
        method: 'PUT',
        body: JSON.stringify({ itemIds }),
      }),
  },

  compat: {
    get: (id: string) => request<{ rules: CompatRule[] }>(`/items/${id}/compat`),
    set: (id: string, mode: 'only' | 'never', itemIds: string[], note?: string) =>
      request<{ ok: true; count: number }>(`/items/${id}/compat`, {
        method: 'PUT',
        body: JSON.stringify({ mode, itemIds, note: note ?? null }),
      }),
  },

  care: {
    open: () => request<CareEvent[]>('/care-events/open'),
    send: (payload: Record<string, unknown>) =>
      request<{ events: string[] }>('/care-events', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },

  push: {
    status: () => request<PushStatus>('/push/status'),
    subscribe: (sub: PushSubscriptionJSON) =>
      request<{ id: string }>('/push/subscriptions', {
        method: 'POST',
        body: JSON.stringify(sub),
      }),
    unsubscribe: (endpoint: string) =>
      request<{ removed: boolean }>('/push/subscriptions', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint }),
      }),
    test: () => request<{ sent: number; failed: number; pruned: number }>('/push/test', {
      method: 'POST',
    }),
  },

  settings: () => request<Record<string, unknown>>('/settings'),
  setSetting: (key: string, value: unknown) =>
    request<{ key: string }>(`/settings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),

  reports: {
    costPerWear: () =>
      request<{
        items: { itemId: string; name: string; costPerWearCents: number | null; totalWears: number }[];
      }>('/reports/cost-per-wear'),
    unworn: () =>
      request<{ itemId: string; name: string; daysSince: number | null }[]>('/reports/unworn'),
    usage: () => request<{ total: number; wornAtLeastOnce: number; share: number }>('/reports/usage'),
    gaps: () => request<{ tempC: number; formality: number; note: string }[]>('/reports/gaps'),
  },

  resetLearning: (id: string) => request<{ ok: true }>(`/learning/${id}/reset`, { method: 'POST' }),

  uploadPhotos: (files: File[]) => {
    const form = new FormData();
    for (const file of files) form.append('files', file);
    return request<{
      photos: { id: string; pathThumb: string; jobId: string | null }[];
      analyse: Analysestatus;
    }>('/photos', {
      method: 'POST',
      body: form,
    });
  },
  ingestJobs: () =>
    request<
      { jobs: Record<string, unknown>[]; usage: Record<string, number> } & Analysestatus
    >('/ingest-jobs'),

  /** Der Entwurf aus der Bildanalyse zu einem Foto — Vorschläge, alles überschreibbar. */
  photoDraft: (photoId: string) =>
    request<
      {
        status: 'pending' | 'running' | 'done' | 'failed';
        draft: Record<string, unknown> | null;
        lowConfidence: string[];
        error: string | null;
        model: string | null;
        attempts: number;
        maxAttempts: number;
      } & Analysestatus
    >(`/photos/${photoId}/draft`),

  /** Stösst die Warteschlange sofort an, statt auf den Zwei-Minuten-Takt zu warten. */
  processIngest: () =>
    request<{ done: number; failed: number }>('/ingest-jobs/process', { method: 'POST' }),

  importCsv: (csv: string) =>
    request<{ created: number; failed: { row: number; message: string }[] }>('/items/import-csv', {
      method: 'POST',
      body: JSON.stringify({ csv }),
    }),
};
